import React, { useState } from "react";
import CommonModal from "components/common-modal";
import isOutOfStock from "helpers/is-out-of-stock";
import notifyOutOfStock from "pms/notify-out-of-stock";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseContact = (value) => {
    const trimmed = value.trim();
    if (trimmed.includes("@")) {
        return EMAIL_REGEX.test(trimmed) ? trimmed : null;
    }

    const digits = trimmed.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 15 ? trimmed : null;
};

const OutOfStockModal = ({ isOpen, onClose, product }) => {
    const [contact, setContact] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleClose = () => {
        setContact("");
        setError("");
        setSubmitted(false);
        onClose();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const parsedContact = parseContact(contact);
        if (!parsedContact) {
            setError("Please enter a valid email or phone number.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            await notifyOutOfStock({ email: parsedContact, product });
            setSubmitted(true);
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <CommonModal
            isOpen={isOpen}
            closeModal={handleClose}
            overlayExtraStyling="!z-[1001]"
            modalExtraStyling="!w-[calc(100%-2rem)] max-w-[36rem] !bg-blue-700 rounded-2xl shadow-xl outline-none"
        >
            <div className="relative px-6 py-10 text-center text-white md:px-12">
                <button
                    type="button"
                    aria-label="Close"
                    onClick={handleClose}
                    className="absolute top-3 right-4 text-2xl leading-none text-white/70 hover:text-white"
                >
                    &times;
                </button>

                <h2 className="mb-4 text-3xl font-bold leading-tight md:text-4xl">
                    {submitted ? "You\u2019re on the List!" : "Get 20% Off When Your Size is Back"}
                </h2>

                {submitted ? (
                    <p className="text-base md:text-lg">
                        We&apos;ll notify you as soon as your size is back in stock, along with your exclusive 20% discount.
                    </p>
                ) : (
                    <>
                        <p className="mb-6 text-base md:text-lg">
                            We&apos;re sorry, your RV cover size is currently sold out. We&apos;ll notify you as soon as it&apos;s back in stock, along with an exclusive 20% discount for waiting.
                        </p>

                        <form onSubmit={handleSubmit} className="text-left" noValidate>
                            <label htmlFor="out-of-stock-contact" className="mb-2 block text-sm font-bold">
                                Email or Phone Number
                                <input
                                    id="out-of-stock-contact"
                                    type="text"
                                    autoComplete="email"
                                    value={contact}
                                    onChange={(event) => setContact(event.target.value)}
                                    placeholder="Enter your email or phone number"
                                    className="mt-2 mb-4 block w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-base font-normal text-blue-700 placeholder-gray-500 outline-none"
                                />
                            </label>
                            {error && <p className="-mt-2 mb-4 text-sm text-red-300">{error}</p>}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-md bg-yellow-600 py-3 text-lg font-bold text-blue-700 transition-opacity hover:opacity-90 disabled:opacity-70"
                            >
                                {loading ? "Submitting..." : "Notify Me & Save 20%"}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </CommonModal>
    );
};

// Usage: `if (checkOutOfStock(product)) return;` before adding to cart, and render `outOfStockModal`
export const useOutOfStockModal = () => {
    const [outOfStockProduct, setOutOfStockProduct] = useState(null);

    const checkOutOfStock = (product) => {
        if (!isOutOfStock(product)) return false;

        setOutOfStockProduct(product);
        return true;
    };

    const outOfStockModal = (
        <OutOfStockModal
            isOpen={Boolean(outOfStockProduct)}
            onClose={() => setOutOfStockProduct(null)}
            product={outOfStockProduct}
        />
    );

    return { checkOutOfStock, outOfStockModal };
};

export default OutOfStockModal;
